/**
 * InvitationsTab — create, list, revoke, resend user invitations.
 * Sprint P37o · Feb 2026.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowClockwise, Copy, EnvelopeSimple, Plus, X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { TabError, TabSkeleton } from "@/pages/superadmin/tabs/RolesTab";

const STATUS_COLOURS = {
  pending:    "bg-blue-50 border-blue-200 text-blue-700",
  accepted:   "bg-emerald-50 border-emerald-200 text-emerald-700",
  expired:    "bg-amber-50 border-amber-200 text-amber-700",
  revoked:    "bg-rose-50 border-rose-200 text-rose-700",
  superseded: "bg-slate-100 border-slate-200 text-slate-500",
};

const ROLE_OPTIONS = [
  "SUPER_ADMIN", "COMPANY_ADMIN", "PROJECT_ADMIN", "COMPETENT_PERSON",
  "GEOLOGIST", "MINING_ENGINEER", "GIS_SPECIALIST", "TECHNICAL_REVIEWER",
  "DATA_MANAGER", "REPORT_VIEWER", "READ_ONLY", "GUEST",
];

export default function InvitationsTab() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [lastToken, setLastToken] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["rbac", "invitations", statusFilter],
    queryFn: () => api.get("/rbac/invitations", {
      params: statusFilter ? { status: statusFilter } : {},
    }).then((r) => r.data),
    staleTime: 15_000,
  });
  const invitations = data?.invitations || [];

  const revokeMut = useMutation({
    mutationFn: (id) => api.post(`/rbac/invitations/${id}/revoke`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rbac", "invitations"] });
      toast.success("Invitation revoked.");
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || "Revoke failed"),
  });
  const resendMut = useMutation({
    mutationFn: (id) => api.post(`/rbac/invitations/${id}/resend`).then((r) => r.data),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["rbac", "invitations"] });
      setLastToken({ token: d.invitation.token, email: d.invitation.email });
      toast.success("Invitation resent — copy the new token.");
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || "Resend failed"),
  });

  if (isLoading) return <TabSkeleton lines={8} />;
  if (error) return <TabError error={error} />;

  const copyToken = async (t) => {
    try { await navigator.clipboard.writeText(t); toast.success("Token copied."); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <div data-testid="rbac-invitations-tab" className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading font-black text-2xl tracking-tight">Invitations</h2>
          <p className="mt-1 text-[12px] text-slate-500 max-w-3xl">
            Single-use, expirable onboarding links. The plaintext token is shown only once at
            creation — the database stores a SHA-256 hash. Rate-limited to 10 invites / hour.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40" data-testid="inv-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="accepted">Accepted</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
              <SelectItem value="revoked">Revoked</SelectItem>
              <SelectItem value="superseded">Superseded</SelectItem>
            </SelectContent>
          </Select>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button className="gap-2 bg-brand-blue hover:bg-brand-blue/90 text-white" data-testid="inv-new-btn">
                <Plus size={14} /> New invitation
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="inv-new-dialog">
              <DialogHeader><DialogTitle>Invite a new user</DialogTitle></DialogHeader>
              <NewInvitationForm onCreated={(inv) => {
                setLastToken({ token: inv.token, email: inv.email });
                setShowCreate(false);
                qc.invalidateQueries({ queryKey: ["rbac", "invitations"] });
              }} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {lastToken && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-[13px]" data-testid="inv-token-banner">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-emerald-800">
              Invitation for <b>{lastToken.email}</b>. Copy the token now — it will not be shown again.
            </div>
            <button
              onClick={() => copyToken(lastToken.token)}
              data-testid="inv-copy-token"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-emerald-300 bg-white text-emerald-800 text-[12px] hover:bg-emerald-100">
              <Copy size={12} /> Copy token
            </button>
          </div>
          <div className="mt-2 font-mono text-[11px] break-all text-emerald-900/70">{lastToken.token}</div>
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        {invitations.length === 0 ? (
          <div className="p-8 text-center text-slate-500" data-testid="inv-empty">
            No invitations {statusFilter ? `with status "${statusFilter}"` : "yet"}. Click <b>New invitation</b> to send one.
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-slate-50 text-[10px] uppercase tracking-[0.15em] text-slate-500">
                  <th className="text-left px-4 py-2.5 border-b border-slate-200">Email</th>
                  <th className="text-left px-4 py-2.5 border-b border-slate-200">Role</th>
                  <th className="text-left px-4 py-2.5 border-b border-slate-200" data-mobile-hide="md">Company</th>
                  <th className="text-left px-4 py-2.5 border-b border-slate-200">Status</th>
                  <th className="text-left px-4 py-2.5 border-b border-slate-200" data-mobile-hide="sm">Invited by</th>
                  <th className="text-left px-4 py-2.5 border-b border-slate-200" data-mobile-hide="xs">Expires</th>
                  <th className="text-right px-4 py-2.5 border-b border-slate-200 w-40">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invitations.map((inv) => (
                  <tr key={inv.id} className="hover:bg-slate-50/70" data-testid={`inv-row-${inv.id}`}>
                    <td className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-1.5">
                      <EnvelopeSimple size={12} className="text-slate-400" />
                      {inv.email}
                    </td>
                    <td className="px-4 py-2.5 border-b border-slate-100 font-mono text-[11.5px]">{inv.role}</td>
                    <td className="px-4 py-2.5 border-b border-slate-100 text-[12px] text-slate-500" data-mobile-hide="md">{inv.company_id || "—"}</td>
                    <td className="px-4 py-2.5 border-b border-slate-100">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] uppercase tracking-wider ${STATUS_COLOURS[inv.status] || STATUS_COLOURS.superseded}`}>
                        {inv.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 border-b border-slate-100 text-[12px] text-slate-500" data-mobile-hide="sm">{inv.invited_by_email || inv.invited_by || "—"}</td>
                    <td className="px-4 py-2.5 border-b border-slate-100 text-[12px] text-slate-500 font-mono" data-mobile-hide="xs">
                      {inv.expires_at ? String(inv.expires_at).slice(0, 10) : "—"}
                    </td>
                    <td className="px-4 py-2.5 border-b border-slate-100 text-right">
                      {inv.status === "pending" && (
                        <>
                          <button
                            onClick={() => resendMut.mutate(inv.id)}
                            disabled={resendMut.isPending}
                            className="inline-flex items-center gap-1 h-7 px-2 rounded-sm border border-slate-300 bg-white text-slate-700 text-[11.5px] hover:bg-slate-50 mr-1"
                            data-testid={`inv-resend-${inv.id}`}>
                            <ArrowClockwise size={11} /> Resend
                          </button>
                          <button
                            onClick={() => revokeMut.mutate(inv.id)}
                            disabled={revokeMut.isPending}
                            className="inline-flex items-center gap-1 h-7 px-2 rounded-sm border border-rose-300 bg-white text-rose-700 text-[11.5px] hover:bg-rose-50"
                            data-testid={`inv-revoke-${inv.id}`}>
                            <X size={11} /> Revoke
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function NewInvitationForm({ onCreated }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("READ_ONLY");
  const [companyId, setCompanyId] = useState("");
  const disabled = useMemo(() => !email || !role, [email, role]);
  const createMut = useMutation({
    mutationFn: (body) => api.post("/rbac/invitations", body).then((r) => r.data),
    onSuccess: (d) => {
      toast.success("Invitation created — copy the token now.");
      onCreated?.(d.invitation);
      setEmail("");
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || "Create failed"),
  });
  return (
    <div className="space-y-3">
      <div>
        <label className="text-[11px] uppercase tracking-widest text-slate-500">Email</label>
        <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@company.com" data-testid="inv-form-email" />
      </div>
      <div>
        <label className="text-[11px] uppercase tracking-widest text-slate-500">Role</label>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger data-testid="inv-form-role"><SelectValue /></SelectTrigger>
          <SelectContent>{ROLE_OPTIONS.map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}</SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-[11px] uppercase tracking-widest text-slate-500">Company ID (optional)</label>
        <Input value={companyId} onChange={(e) => setCompanyId(e.target.value)} placeholder="leave blank for own company" data-testid="inv-form-company" />
      </div>
      <div className="pt-2 flex justify-end">
        <Button disabled={disabled || createMut.isPending}
                onClick={() => createMut.mutate({ email, role, company_id: companyId || null })}
                className="bg-brand-blue hover:bg-brand-blue/90 text-white gap-2"
                data-testid="inv-form-submit">
          <Plus size={14} /> Create invitation
        </Button>
      </div>
    </div>
  );
}
